<script setup>
import { ref, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  subMenuPosition: {
    type: String,
    default: 'right',
    validator: value => {
      return ['right', 'left', 'bottom'].includes(value);
    },
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);
const searchQuery = ref('');
const searchPlaceholder = 'Search...';
const noResultsText = 'No results found';

const labelValue = computed(() => props.label);

const filteredOptions = computed(() => {
  if (!props.showSearch || !searchQuery.value) {
    return props.options;
  }
  const query = searchQuery.value.toLowerCase();
  return props.options.filter(option =>
    option.label.toLowerCase().includes(query)
  );
});

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) {
    searchQuery.value = '';
  }
};

const handleSelect = value => {
  emit('update:modelValue', value);
  isOpen.value = false;
  searchQuery.value = '';
};
</script>

<template>
  <div
    v-on-clickaway="() => (isOpen = false)"
    class="relative flex flex-col gap-1 w-fit"
  >
    <Button
      icon="i-lucide-chevron-down"
      size="sm"
      trailing-icon
      color="slate"
      variant="faded"
      class="!w-fit max-w-40"
      :class="{ 'dark:!bg-n-alpha-2 !bg-n-slate-9/20': isOpen }"
      :label="labelValue"
      @click="toggleMenu"
    />
    <div
      v-if="isOpen"
      class="absolute select-none w-56 flex flex-col bg-n-alpha-3 backdrop-blur-[100px] top-0 shadow-xl z-40 rounded-xl border border-n-weak dark:border-n-strong/50"
      :class="{
        'ltr:left-full rtl:right-full ltr:ml-1 rtl:mr-1':
          subMenuPosition === 'right',
        'ltr:right-full rtl:left-full ltr:mr-1 rtl:ml-1':
          subMenuPosition === 'left',
        'top-full mt-2 ltr:right-0 rtl:left-0': subMenuPosition === 'bottom',
      }"
    >
      <div v-if="showSearch" class="p-2">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="searchPlaceholder"
          class="w-full px-2.5 py-1.5 text-xs rounded-lg bg-n-alpha-2 border border-n-weak focus:border-n-blue-9 focus:ring-1 focus:ring-n-blue-9 focus:outline-none text-n-slate-12 placeholder:text-n-slate-11 transition-all"
          @click.stop
        />
      </div>
      <div class="max-h-80 overflow-y-auto px-2 pb-2">
        <button
          v-for="option in filteredOptions"
          :key="option.value"
          type="button"
          class="w-full text-left px-3 py-2.5 text-sm rounded-lg hover:bg-n-alpha-2 text-n-slate-12 flex items-center justify-between transition-all group"
          :class="{
            'bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11':
              option.value === modelValue,
          }"
          @click="handleSelect(option.value)"
        >
          <span class="font-medium">{{ option.label }}</span>
          <i
            v-if="option.value === modelValue"
            class="i-lucide-check w-4 h-4 text-n-blue-11"
          />
        </button>
      </div>
      <div
        v-if="showSearch && filteredOptions.length === 0"
        class="px-3 py-4 text-sm text-n-slate-11 text-center"
      >
        {{ noResultsText }}
      </div>
    </div>
  </div>
</template>
